import {
    createDisposer,
    type Maybe,
    createLogPrefix,
    type Disposable,
    globalRef,
    logDebug,
    logWarn,
    type LogTarget,
    requestWithRetry,
} from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { getDeviceInfo } from '@/device/device'
import { getDeviceConfig } from '@/device/deviceConfig'
import { metricAppVersion } from '@/metrics/metricContext'
import { getApiHost } from '@/service/useLiveHost'
import { documentEvents, windowEvents } from '@/util/interaction'
import { errorMessage } from '@/util/errorUtil'

// The MusicMetrix (AMTC) uploader every metric family shares: batches each action
// onto the wire contract GH's `@amzn/music-metrix-sdk-typescript` `HttpReporter`
// reports, then POSTs it. Emission is observational: `enqueue` never throws, and
// a failed upload is logged and dropped.

// Metrix SDK `amtcEventsEndpoint`; amp targets the `.com` host, so no `/ZAZ` prefix.
const METRIX_INGESTION_PATH = '/telemetry/v1/events'

const DEFAULT_BATCH_SIZE = 20

/** One action, ready for the AMTC `clientActionList`. */
export interface MetrixAction {
    readonly actionName: string
    /** Epoch SECONDS, fractional (not floored), as the AMTC wire carries it. */
    readonly timestamp: number
    readonly additionalDetails: Record<string, string>
    /** GH's `ContentInfo` rows, every value a string, as the MIS model declares them. */
    readonly contentInfo?: readonly Record<string, string>[]
}

/** Raw `additionalDetails`; a null/undefined attribute is dropped from the payload. */
export type WireDetails = Readonly<Record<string, Maybe<string | number | boolean>>>

/** The wire carries every attribute as a string. */
export function toAdditionalDetails(raw: WireDetails): Record<string, string> {
    const out: Record<string, string> = {}
    for (const [key, value] of Object.entries(raw)) {
        if (value != null) out[key] = typeof value === 'string' ? value : String(value)
    }
    return out
}

// GH's value until the metrics owners settle amp's own (Blackbolt's Cradle profiles count only BLACKBOLT).
export const METRIX_PRODUCT_VERSION = 'GREENHORNET'

const DEVICE_LOCALE = /^[a-z]{2}-[A-Z]{2}$/

// The browser's locale, as the SDK's LocaleCollector reads it; the app's display language is `appLocale`.
function deviceLocale(navigator: Navigator): string {
    const { language } = navigator
    return DEVICE_LOCALE.test(language) ? language.replace('-', '_') : 'en_US'
}

function deviceProperties(): string | undefined {
    if (typeof window === 'undefined') return undefined
    const { innerWidth: width, innerHeight: height } = window
    return JSON.stringify({
        displayWidthPixels: width,
        displayHeightPixels: height,
        viewType: width > height ? 'LANDSCAPE' : 'PORTRAIT',
    })
}

/**
 * What GH's Metrix SDK stamps on every action of every family: its `globalAttributes`
 * and the web collectors' `locale`, `userAgent` and `deviceProperties`. They override
 * the action's own, as the SDK's merge does.
 */
function metrixGlobalDetails(): WireDetails {
    const browser = typeof navigator === 'undefined' ? undefined : navigator
    return {
        locale: browser ? deviceLocale(browser) : 'en_US',
        productVersion: METRIX_PRODUCT_VERSION,
        productVersionDetail: metricAppVersion,
        userAgent: browser?.userAgent,
        deviceProperties: deviceProperties(),
    }
}

/** One row of the AMTC `clientActionList` (the SDK's `ClientAction`). */
interface ClientAction extends MetrixAction {
    readonly contentInfo: readonly Record<string, string>[]
}

/**
 * The AMTC ingestion payload — a SINGLE object per batch (the SDK's
 * `FormattedMetrixPayload`), NOT an array. Identity fields are top-level and the
 * per-event rows live under `clientActionList`.
 */
interface FormattedMetrixPayload {
    readonly customerId?: string
    readonly deviceId: string
    readonly deviceType: string
    readonly clientActionList: readonly ClientAction[]
}

interface RequestAuth {
    readonly headers: Record<string, string>
    readonly credentials?: RequestCredentials
    readonly customerId?: string
    readonly deviceId: string
    readonly deviceType: string
}

export class MetrixUploader implements LogTarget, Disposable {
    logPrefix = createLogPrefix('MetrixUploader')

    private readonly buffer: MetrixAction[] = []
    private readonly disposer = createDisposer()
    private pageHidden = false

    constructor(private readonly batchSize: number = DEFAULT_BATCH_SIZE) {
        // Last chance to ship a sub-threshold buffer. Both signals are needed:
        // `pagehide` covers unload/bfcache, `visibilitychange` covers tab-switch.
        this.disposer.add(
            windowEvents.on('pagehide', () => {
                this.pageHidden = true
                this.flush({ keepalive: true })
            })
        )
        this.disposer.add(
            windowEvents.on('pageshow', () => {
                this.pageHidden = false
            })
        )
        this.disposer.add(
            documentEvents.on('visibilitychange', () => {
                if (document.visibilityState === 'hidden') this.flush({ keepalive: true })
            })
        )
    }

    enqueue(action: MetrixAction): void {
        this.buffer.push({
            ...action,
            additionalDetails: {
                ...action.additionalDetails,
                ...toAdditionalDetails(metrixGlobalDetails()),
            },
        })
        // Past the page-hide flush nothing else would ship a late event, e.g. an unload's systemStop.
        if (this.pageHidden) this.flush({ keepalive: true })
        else if (this.buffer.length >= this.batchSize) this.flush()
    }

    flush(options?: { readonly keepalive?: boolean }): void {
        if (this.buffer.length === 0) return
        const batch = this.buffer.splice(0, this.buffer.length)
        // Never retried past requestWithRetry's own retry.
        this.upload(batch, options?.keepalive ?? false).catch((error) =>
            logWarn(this, 'upload failed', errorMessage(error))
        )
    }

    dispose(): void {
        this.flush({ keepalive: true })
        this.disposer.dispose()
    }

    private async upload(batch: readonly MetrixAction[], keepalive: boolean): Promise<void> {
        if (getAuth().usesBearer()) {
            logDebug(this, 'skip upload (bearer auth cannot authenticate AMTC — would 403)', {
                count: batch.length,
            })
            return
        }
        const auth = buildRequestAuth()
        const payload: FormattedMetrixPayload = {
            ...(auth.customerId ? { customerId: auth.customerId } : null),
            deviceId: auth.deviceId,
            deviceType: auth.deviceType,
            clientActionList: batch.map((action) => ({
                ...action,
                contentInfo: action.contentInfo ?? [],
            })),
        }
        logDebug(this, 'upload', { count: batch.length, keepalive })
        await requestWithRetry(`${getApiHost()}${METRIX_INGESTION_PATH}`, {
            method: 'POST',
            headers: auth.headers,
            ...(auth.credentials ? { credentials: auth.credentials } : null),
            // keepalive lets an in-flight page-hide POST outlive the document.
            ...(keepalive ? { keepalive: true } : null),
            body: JSON.stringify(payload),
        })
    }
}

/**
 * Assemble the ingestion request's auth headers + identity from the `Auth`
 * capability, as `gql.ts` / `relayAuthHeaders.ts` do — no hand-rolled crypto or
 * SigV4. Only the cookie-session path is built (the uploader gates bearer auth out
 * first): no `x-amz-access-token`, the CSRF triple + device headers ride along, and
 * `credentials: 'include'` carries the first-party session cookie. Auth/csrf stay
 * OUT of the POST body — putting them there is how GH's GREENH-332 leaked.
 */
function buildRequestAuth(): RequestAuth {
    const auth = getAuth()
    const { deviceId } = getDeviceInfo()
    const { deviceTypeId } = getDeviceConfig()

    const headers: Record<string, string> = {
        'content-type': 'application/json',
        // AMTC ingestion reads `x-amz-*` device headers (the Metrix SDK's
        // `AuthHeaders`), NOT the `x-amzn-*` spelling Firefly `gql.ts` sends.
        'x-amz-device-id': deviceId,
        'x-amz-device-type': deviceTypeId,
        ...(auth.getCsrfHeaders?.() ?? {}),
    }

    const customerId = auth.getCustomerId?.() ?? undefined
    if (customerId) headers['x-amz-customer-id'] = customerId
    if (typeof location !== 'undefined' && location.href) {
        headers['x-amz-request-url'] = location.href
    }

    const credentials = auth.requestCredentials
    return {
        headers,
        ...(credentials ? { credentials } : null),
        ...(customerId ? { customerId } : null),
        deviceId,
        deviceType: deviceTypeId,
    }
}

export const metrixUploaderRef = globalRef(() => new MetrixUploader())

export function getMetrixUploader(): MetrixUploader {
    return metrixUploaderRef.value
}
